import { observer } from 'mobx-react';
import { FC, useMemo } from 'react';

import { CampaignsStore } from '@servicetitan/application-data/campaigns';
import { FeaturesStore } from '@servicetitan/application-data/features';
import { useDependencies } from '@servicetitan/react-ioc';
import {
    DateRangeColumnMenuFilter,
    StandardColumnMenuFilter,
    Table,
    TableColumn,
    fieldValuesColumnMenuFilter,
} from '@servicetitan/table';

import { PhoneNumbersStore } from '../stores/phone-numbers.store';
import { PhoneNumbersActionCell } from './phone-numbers-action-cell';
import * as Styles from './phone-numbers-action-cell.module.less';
import {
    CallContinuityNumberCell,
    CampaignCell,
    DefaultCallTypeCell,
    IncomingSmsCell,
    IsTollFreeCell,
    MessagingCell,
    PhoneNumberCell,
    TollFreeStatusCell,
} from './phone-numbers-cells';
import { TollFreeNumberVerificationStatus } from '@servicetitan/application-data/telecom';

export const PhoneNumbersGrid: FC<{ canEdit: boolean }> = observer(props => {
    const [{ phoneNumbersGridState }, { campaigns }, { features }] = useDependencies(
        PhoneNumbersStore,
        CampaignsStore,
        FeaturesStore
    );

    const fieldValuesFilter = useMemo(
        () => fieldValuesColumnMenuFilter(phoneNumbersGridState),
        [phoneNumbersGridState]
    );
    const campaignFilter = useMemo(
        () => (props: any) => (
            <div className={Styles.filter}>
                {fieldValuesColumnMenuFilter(phoneNumbersGridState, id => {
                    const campaign = campaigns.find(x => x.id === id);
                    return campaign ? campaign.name : 'Not specified';
                })(props)}
            </div>
        ),
        [phoneNumbersGridState, campaigns]
    );
    const tollFreeStatusColumnFilter = useMemo(
        () => (props: any) => (
            <div className={Styles.filter}>
                {fieldValuesColumnMenuFilter(phoneNumbersGridState, columnValue => {
                    return typeof columnValue === 'number'
                        ? TollFreeNumberVerificationStatus[columnValue as number]
                        : 'N/A';
                })(props)}
            </div>
        ),
        [phoneNumbersGridState]
    );

    return (
        <Table
            sortable
            rowHeight={50}
            className="m-t-4 m-b-3"
            tableState={phoneNumbersGridState}
            striped={false}
        >
            <TableColumn
                width={180}
                field="friendlyName"
                title="Phone Number"
                locked
                cell={PhoneNumberCell}
                columnMenu={StandardColumnMenuFilter}
            />
            <TableColumn
                width={210}
                field="forwardTo"
                title={
                    features.EnableServiceTitanPhonesUser
                        ? 'Call Continuity Number'
                        : 'Forwarding Number'
                }
                cell={CallContinuityNumberCell}
                columnMenu={StandardColumnMenuFilter}
            />
            <TableColumn
                width={120}
                field="dateAdded"
                title="Date Added"
                format="{0:d}"
                editable={false}
                columnMenu={DateRangeColumnMenuFilter}
            />
            <TableColumn
                width={190}
                field="addedBy"
                title="Added By"
                editable={false}
                columnMenu={fieldValuesFilter}
            />
            <TableColumn width={120} field="isTollFree" title="Toll-Free" cell={IsTollFreeCell} />
            <TableColumn
                width={120}
                field="enableSms"
                title="Incoming SMS"
                cell={IncomingSmsCell}
            />
            {features.EnableDefaultCallType && (
                <TableColumn
                    width={140}
                    field="defaultCallType"
                    title="Default Call Type"
                    cell={DefaultCallTypeCell}
                />
            )}
            {features.EnableCustomOutboundCallerId && (
                <TableColumn
                    width={160}
                    field="alias"
                    title="Outbound Caller ID"
                    editable={false}
                />
            )}
            {features.EnableTollFreeNumberVerificationStatus && (
                <TableColumn
                    width={200}
                    field="tollFreeNumberVerificationStatus"
                    title="Toll-free Texting Status"
                    editable={false}
                    cell={TollFreeStatusCell}
                    columnMenu={tollFreeStatusColumnFilter}
                />
            )}
            {features.EnableTcrCampaignIdColumn && (
                <TableColumn
                    width={160}
                    field="tcrCampaignExternalId"
                    title="TCR Campaign ID"
                    columnMenu={fieldValuesFilter}
                />
            )}
            <TableColumn
                width={210}
                field="campaignId"
                title="Campaign"
                cell={CampaignCell}
                columnMenu={campaignFilter}
            />
            <TableColumn width={100} field="tcrUseCases" title="Messaging" cell={MessagingCell} />
            {props.canEdit && (
                <TableColumn
                    width={48}
                    locked
                    cell={PhoneNumbersActionCell}
                    headerClassName={Styles.actionButtonsHeader}
                />
            )}
        </Table>
    );
});
